import React, { useState } from "react";
import classNames from "classnames";
import LikeButton from "../LikeButton";
import { MdDateRange, MdZoomIn } from "react-icons/md";
import { IoLocationOutline } from "react-icons/io5";
import { optimizeImage } from "../../utilities/imageOptimization";

import { Photo } from "../../types/photo";
import { PhotoLikeResponse } from "../../types/photo";

export interface FlipCardProps {
  photo: Photo;
  liked: boolean;
  likeCount: number;

  onLikeToggle?: (photoId: number, liked: boolean, likeCount: number) => void;
  onLikeAction?: (photoId: number) => Promise<PhotoLikeResponse>;
  onOpenLightbox?: (photoId: number) => void;
  hue?: number;
}

const FlipCard: React.FC<FlipCardProps> = ({
  photo,
  liked,
  likeCount,
  onLikeToggle,
  onLikeAction,
  onOpenLightbox,
  hue = 220
}) => {
  const [flipped, setFlipped] = useState(false);
  const [internalLiked, setInternalLiked] = useState(liked);
  const [internalLikeCount, setInternalLikeCount] = useState(likeCount);

  const handleLikeToggle = (newLiked: boolean, newCount: number) => {
    setInternalLiked(newLiked);
    setInternalLikeCount(newCount);
    onLikeToggle?.(photo.photoId, newLiked, newCount);
  };

  const handleOpenLightbox = (e: React.MouseEvent) => {
    e.stopPropagation();
    onOpenLightbox?.(photo.photoId);
  };

  const imageUrl = photo.src || `/images/default.jpg`; // fallback
  // Optimize the image for card display (400x300)
  const optimizedImageUrl = optimizeImage(imageUrl, 'card');
  const location = photo.address || "Unknown";
  const displayDate = photo.timestamp
    ? new Date(photo.timestamp).toLocaleDateString()
    : "Unknown";

  return (
    <div
      className="w-[310px] h-[500px] m-5 [perspective:1000px] cursor-pointer"
      style={{ "--hue": hue.toString() } as React.CSSProperties}
      onClick={() => setFlipped(prev => !prev)}
      onDoubleClick={handleOpenLightbox}
    >
      <div
        className={classNames(
          "relative w-full h-full transition-transform duration-500 [transform-style:preserve-3d]",
          { "[transform:rotateY(180deg)]": flipped }
        )}
      >
        {/* Front Side */}
        <div className="absolute w-full h-full rounded-2xl overflow-hidden [backface-visibility:hidden]">
          <img src={optimizedImageUrl} alt={photo.title} className="w-full h-full object-cover" />
          <button
            className="absolute top-3 right-3 p-2 bg-black/60 rounded-full"
            onClick={handleOpenLightbox}
          >
            <MdZoomIn className="w-6 h-6 text-white" />
          </button>
        </div>

{/* Back Side */}
<div className="absolute inset-0 rounded-2xl overflow-hidden [backface-visibility:hidden] [transform:rotateY(180deg)]">
  <img
    src={optimizedImageUrl}
    alt="Background"
    className="absolute inset-0 w-full h-full object-cover"
  />
  
  <div className="absolute inset-0 bg-black/85 px-4 pt-10 pb-4 flex flex-col justify-between text-white overflow-hidden">
    <div className="space-y-1 overflow-y-auto max-h-[75%] pr-1">
      <h2 className="text-xl font-bold text-center break-words">{photo.title || "Untitled"}</h2>
      
      {photo.description && (
        <p className="text-sm text-gray-200 pt-4 whitespace-pre-wrap text-left">
          {photo.description}
        </p>
      )}
      
      {photo.uploadedBy?.name && (
        <p className="text-sm text-right text-gray-300 mt-4">By: {photo.uploadedBy.name}</p>
      )}
    </div>

    <div className="mt-4 space-y-2 text-sm text-gray-200">
      <div className="flex justify-between items-center">
        <LikeButton
          photoId={photo.photoId}
          initialLiked={internalLiked}
          initialLikeCount={internalLikeCount}
          onToggle={handleLikeToggle}
          onLikeAction={onLikeAction}
          size="text-sm"
          iconSize="w-5 h-5"
          customTextColor="text-gray-200 hover:text-red-500"
        />
        <div className="flex items-center space-x-1">
          <MdDateRange className="w-5 h-5" />
          <span>{displayDate}</span>
        </div>
      </div>

      <div className="flex items-center">
        <IoLocationOutline className="w-5 h-5 shrink-0" />
        <span className="ml-2 break-words">{location}</span>
      </div>
    </div>
  </div>
</div>

      </div>
    </div>
  );
};

export default FlipCard;
